import useSWR from 'swr';
import { useMemo } from 'react';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import useLocation from './use-location';
import useSavedAddresses from './use-saved-addresses';
import { DropShopProviderResponse, ShopProviders } from '~/lib/types/edge-api-types';
import { EDGE_API } from '~/lib/constants';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';

type UseDropShopProvidersParams = {
  isoCode?: string;
  postcode?: string;
  useGeo?: boolean;
  onGeoError?: (error: GeolocationPositionError) => void;
};

export default function useDropShopProviders({ postcode, useGeo, isoCode, onGeoError }: UseDropShopProvidersParams) {
  const { session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const { defaultAddress, loading } = useSavedAddresses(isoCode, undefined);
  const location = useLocation({
    postcode: postcode || defaultAddress?.postcode,
    ready: !loading,
    useGeo: useGeo,
    onGeoError: onGeoError,
    isoCode: isoCode
  });

  const { data, isLoading } = useSWR<DropShopProviderResponse>(
    !location.loading && location.coordinates
      ? `${EDGE_API}/${tenant}/order-process/dropshops/${isoCode || 'GBR'}/${location.coordinates?.latitude}/${location.coordinates?.longitude}`
      : null,
    (url: string) =>
      fetchWithTenantAnalytics(tenant, settings, session, url)
        .then((res) => res.json())
        .catch(capturePromiseException),
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: true, revalidateOnMount: true }
  );

  const shopProviders: ShopProviders = useMemo(() => {
    return data?.result?.shops || {};
  }, [data]);

  return {
    shopProviders,
    loading: isLoading
  };
}
